import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { Spinner } from '../../components/ui/Spinner'
import { citeste, lei, scrie, useCifre, type Cifra } from './date'

const MAXIM = 4

/**
 * Cifrele alese (cel mult patru) sau, daca omul n-a ales, cele implicite pentru rolul lui.
 * Alegerea se tine in browser; o cifra la care omul nu mai are drept dispare singura, fiindca
 * serverul nu o mai trimite.
 */
export function CifreAzi({
  utilizator,
  alege,
  laAles,
}: {
  utilizator: number | undefined
  alege: boolean
  laAles: () => void
}) {
  const q = useCifre()
  const cheie = `b4l.acasa.cifre.${utilizator ?? 0}`
  const [alese, setAlese] = useState<string[] | null>(() => citeste<string[] | null>(cheie, null))

  if (q.isLoading)
    return (
      <div className="flex justify-center py-6">
        <Spinner label="Se calculează…" />
      </div>
    )
  if (q.isError || !q.data)
    return (
      <p className="text-mic text-b4l-text-muted">
        Cifrele nu s-au putut calcula acum.{' '}
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="font-semibold text-b4l-blue hover:underline"
        >
          Reîncearcă
        </button>
      </p>
    )

  const { cifre, implicite } = q.data
  const dupaId = new Map(cifre.map((c) => [c.id, c]))
  const ids = (alese ?? implicite).filter((id) => dupaId.has(id)).slice(0, MAXIM)
  const deAratat = ids.map((id) => dupaId.get(id)!)

  if (alege)
    return (
      <AlegeCifre
        cifre={cifre}
        alese={ids}
        laGata={(noi) => {
          scrie(cheie, noi)
          setAlese(noi)
          laAles()
        }}
        laImplicit={() => {
          scrie(cheie, null)
          setAlese(null)
          laAles()
        }}
      />
    )

  // O cifra care n-a putut fi calculata nu dispare pe tacute.
  const lipsa = q.data.neverificate.length
  const aviz =
    lipsa > 0 ? (
      <p role="status" className="text-mic text-b4l-amber-dark">
        {lipsa === 1 ? 'O cifră n-a putut fi calculată' : `${lipsa} cifre n-au putut fi calculate`}{' '}
        acum.{' '}
        <button type="button" onClick={() => void q.refetch()} className="font-semibold underline">
          Reîncearcă
        </button>
      </p>
    ) : null

  if (deAratat.length === 0)
    return (
      <div className="grid gap-2">
        {aviz}
        {lipsa === 0 && (
          <p className="text-mic text-b4l-text-muted">Nicio cifră pentru modulele tale.</p>
        )}
      </div>
    )

  return (
    <div className="grid gap-2">
      {aviz}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {deAratat.map((c) => (
          <CardCifraAzi key={c.id} c={c} />
        ))}
      </div>
    </div>
  )
}

function CardCifraAzi({ c }: { c: Cifra }) {
  const directie =
    c.inainte === undefined
      ? null
      : c.valoare > c.inainte
        ? 'sus'
        : c.valoare < c.inainte
          ? 'jos'
          : 'egal'
  const bine = directie === 'egal' || directie === null ? null : directie === c.sens
  const Sageata = directie === 'sus' ? ArrowUpRight : directie === 'jos' ? ArrowDownRight : Minus
  return (
    <Link
      to={c.link}
      className="grid content-start gap-1 rounded-caseta border border-b4l-border bg-b4l-surface p-3 hover:border-b4l-blue/60"
    >
      <span className="text-titlu tabular-nums text-b4l-text">
        {c.unitate === 'lei' ? lei(c.valoare) : c.valoare.toLocaleString('ro-RO')}
      </span>
      <span className="text-mic text-b4l-text-muted">{c.eticheta}</span>
      {c.comparatie && (
        <span
          className={`flex items-center gap-1 text-mic ${
            bine === null ? 'text-b4l-text-dim' : bine ? 'text-b4l-green' : 'text-b4l-red'
          }`}
        >
          {directie && <Sageata size={13} aria-hidden="true" />}
          {c.comparatie}
        </span>
      )}
      {c.serie && c.serie.some((v) => v > 0) && <Linie serie={c.serie} />}
    </Link>
  )
}

/** Ultimele 7 zile, pe aceeasi scara: zero jos, maximul sus. */
function Linie({ serie }: { serie: number[] }) {
  const max = Math.max(...serie, 1)
  const w = 100
  const h = 24
  const pas = w / (serie.length - 1)
  const puncte = serie.map(
    (v, i) => `${(i * pas).toFixed(1)},${(h - (v / max) * (h - 2) - 1).toFixed(1)}`,
  )
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="mt-1 hidden h-6 w-full text-b4l-blue sm:block"
      role="img"
      aria-label={`Ultimele 7 zile: ${serie.join(', ')}`}
    >
      <polyline points={puncte.join(' ')} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

function AlegeCifre({
  cifre,
  alese,
  laGata,
  laImplicit,
}: {
  cifre: Cifra[]
  alese: string[]
  laGata: (ids: string[]) => void
  laImplicit: () => void
}) {
  const [sel, setSel] = useState<string[]>(alese)
  const comuta = (id: string) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < MAXIM ? [...s, id] : s))
  return (
    <div className="grid gap-3 rounded-caseta border border-b4l-border bg-b4l-surface p-3">
      <p className="text-mic text-b4l-text-muted">
        Alege cel mult {MAXIM} cifre. Apar doar cele din modulele la care ai acces.
      </p>
      <ul className="grid gap-1 sm:grid-cols-2">
        {cifre.map((c) => {
          const bifata = sel.includes(c.id)
          return (
            <li key={c.id}>
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-corp hover:bg-b4l-bg ${
                  !bifata && sel.length >= MAXIM ? 'opacity-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={bifata}
                  disabled={!bifata && sel.length >= MAXIM}
                  onChange={() => comuta(c.id)}
                />
                <span className="text-b4l-text">{c.eticheta}</span>
              </label>
            </li>
          )
        })}
      </ul>
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={laImplicit}
          className="rounded-md px-3 py-1.5 text-corp text-b4l-text-muted hover:bg-b4l-bg"
        >
          Cele implicite
        </button>
        <button
          type="button"
          onClick={() => laGata(sel)}
          disabled={sel.length === 0}
          className="rounded-md bg-b4l-blue px-3 py-1.5 text-corp font-semibold text-b4l-pe-accent disabled:opacity-50"
        >
          Gata
        </button>
      </div>
    </div>
  )
}
